import { AlertTriangle, ArrowDownRight } from 'lucide-react';
import { formatEuro } from '../utils/currency';
import { getStatusBadgeClass } from '../utils/statusColors';
import type { UnprofitableCampaign } from '../types/statistics';

interface UnprofitableCampaignsProps {
  campaigns: UnprofitableCampaign[];
  loading?: boolean;
}

const statusLabels: Record<string, string> = {
  'Consultation': 'Consulta',
  'Confirmed': 'Confirmada',
  'In Progress': 'Em Progresso',
  'Billing': 'Faturação',
  'Completed': 'Concluída',
  'Production': 'Produção',
};

export function UnprofitableCampaigns({ campaigns, loading }: UnprofitableCampaignsProps) {
  if (loading) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 p-6 shadow-sm animate-pulse">
        <div className="h-6 w-64 bg-red-200/50 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-red-100/50 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) return null;

  const totalLoss = campaigns.reduce((sum, c) => sum + c.profit, 0);

  return (
    <div className="rounded-xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="bg-red-500 rounded-xl p-3">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Campanhas com Margem Negativa
            </h2>
            <p className="text-gray-600 text-sm">
              {campaigns.length} {campaigns.length === 1 ? 'campanha onde' : 'campanhas onde'} o custo pago excede a receita recebida
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Perda Total</p>
          <p className="text-xl font-bold text-red-600">{formatEuro(totalLoss)}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-red-200/60">
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Campanha</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Marca</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Cliente</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Influencer</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Receita</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Custo</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Resultado</th>
              <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-3">Estado</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr
                key={campaign.id}
                className="border-b border-red-100/60 hover:bg-red-100/30 transition-colors"
              >
                <td className="py-3 px-3">
                  <span className="font-medium text-gray-900 text-sm">{campaign.campaign_ref}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-gray-700 text-sm">{campaign.brand}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-gray-700 text-sm">{campaign.client}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-gray-700 text-sm">{campaign.influencer}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-sm text-emerald-600 font-medium">{formatEuro(campaign.client_revenue)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-sm text-red-600 font-medium">{formatEuro(campaign.influencer_cost)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-red-600">
                    <ArrowDownRight className="w-3.5 h-3.5" />
                    {formatEuro(campaign.profit)}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(campaign.campaign_status, 'soft')}`}>
                    {statusLabels[campaign.campaign_status] || campaign.campaign_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
